/**
 * Saidiseire (customer site monitoring).
 *
 *   /monitoring       - the desk: every watched site, the engine, the counts
 *   /monitoring/:id   - one site: 90 days, today's response times, outages
 *
 * The section is `view:crm`, like every CRM register: anybody on the desk may
 * see whether a customer's site is up. Every change - adding a site, pausing
 * one, the engine switch, who gets alerted - answers to `manage:crm.monitoring`,
 * and the controls for it are not drawn for somebody without that key. The
 * server checks the same key on every write; hiding them here only spares that
 * person a button that would be refused.
 *
 * One `PopHost` for both pages, because the row menu and the recipients field
 * are `Pop` menus and a `Pop` without a host above it never opens.
 */
import { Navigate, Route, Routes } from "react-router-dom";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { PopHost } from "../../shell/Pop";
import { MonitorPage } from "./MonitorPage";
import { MonitoringDesk } from "./MonitoringDesk";

/** The platform key every write in this section needs. */
export const MONITORING_WRITE_PERM = "manage:crm.monitoring";
/** The package editor's key: a site page links to it only for somebody who can open it. */
const PLANS_PERM = "manage:plans";

export function MonitoringSection({ user }: { user: StaffUser }) {
  const t = useT();
  const canManage = userCanPlatform(user, MONITORING_WRITE_PERM);
  const canEditPlans = userCanPlatform(user, PLANS_PERM);
  return (
    <PopHost>
      <Routes>
        <Route index element={<MonitoringDesk canManage={canManage} t={t} />} />
        <Route path=":id" element={<MonitorPage canManage={canManage} canEditPlans={canEditPlans} t={t} />} />
        <Route path="*" element={<Navigate to="/monitoring" replace />} />
      </Routes>
    </PopHost>
  );
}
